import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  getTimeClockStatus, getMyClock, clockIn, clockOut, clockInMe, clockOutMe,
  getTimeEntries, getCompanySettings, fmtDuration,
  createTimeEntry, updateTimeEntry, deleteTimeEntry, fetchFileBlobUrl,
  getTimeClockSummary, getLeaves, createLeave, deleteLeave,
  getHolidays, createHoliday, deleteHoliday,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, LogIn, LogOut, Users, RefreshCw, Timer, Pencil, Trash2, Plus, ExternalLink, Copy, ImageOff, Coffee, TrendingDown, Palmtree, CalendarDays } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const pad = (n) => String(n).padStart(2, "0");
const fmtLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => fmtLocalDate(new Date());
const timeOf = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "-"; }
};
// ISO (UTC) -> value for <input type="datetime-local"> in local time
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const localInputToIso = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const storagePathFromUrl = (url) => (url ? String(url).replace(/^\/api\/files\//, "") : "");

// Monday = 0 … Sunday = 6
const dowMon = (dateStr) => {
  try { return (new Date(`${dateStr}T12:00:00`).getDay() + 6) % 7; }
  catch { return 0; }
};
const rangeFor = (view, anchor) => {
  const base = new Date(`${anchor}T12:00:00`);
  if (view === "week") {
    const dow = (base.getDay() + 6) % 7;
    const start = new Date(base); start.setDate(base.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
  }
  if (view === "month") {
    const start = new Date(base.getFullYear(), base.getMonth(), 1);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
  }
  return { day: anchor };
};

// Like rangeFor but always yields an explicit {start,end} window (day view => single day).
const summaryRangeFor = (view, anchor) => {
  if (view === "day") return { start: anchor, end: anchor };
  return rangeFor(view, anchor);
};

// Authenticated photo thumbnail (img cannot send the auth header -> fetch as blob)
function PhotoThumb({ url, label }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const path = storagePathFromUrl(url);
  useEffect(() => {
    if (!path) return undefined;
    let active = true;
    let made = null;
    fetchFileBlobUrl(path)
      .then((u) => { if (active) { made = u; setSrc(u); } else { URL.revokeObjectURL(u); } })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  if (!path) return null;
  if (failed) return <ImageOff className="h-8 w-8 text-muted-foreground" aria-label={label} />;
  if (!src) return <div className="h-9 w-9 animate-pulse rounded bg-secondary" />;
  return (
    <a href={src} target="_blank" rel="noreferrer" title={label} data-testid="clock-photo-thumb">
      <img src={src} alt={label} className="h-9 w-9 rounded border border-border object-cover transition-transform hover:scale-110" />
    </a>
  );
}

const emptyForm = { id: null, operator_id: "", clock_in_at: "", clock_out_at: "", break_minutes: "", notes: "" };

export default function TimeClock() {
  const { t } = useT();
  const { can } = useAuth();
  const isManager = can("admin", "manager");
  const [status, setStatus] = useState({ operators: [], clocked_in_count: 0, total_today_seconds: 0 });
  const [me, setMe] = useState({ operator: null, open_entry: null, today_seconds: 0 });
  const [day, setDay] = useState(todayStr());
  const [view, setView] = useState("day");
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState({ standard_workday_hours: 8, working_days: [0, 1, 2, 3, 4] });
  const [busy, setBusy] = useState(false);

  // Admin edit state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // History filters
  const [filterOp, setFilterOp] = useState("all");
  const [weekendOnly, setWeekendOnly] = useState(false);
  // Weekly overtime / deficit summary (computed on the backend per ISO week)
  const [summary, setSummary] = useState({ total_overtime_secs: 0, total_deficit_secs: 0, weeks: [] });
  // Leave & holidays management
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ open: false, operator_id: "", start_date: "", end_date: "", type: "leave", note: "" });
  const [holidayForm, setHolidayForm] = useState({ open: false, date: "", name: "" });
  const [savingOff, setSavingOff] = useState(false);

  const kioskUrl = `${window.location.origin}/kiosk`;

  const workingDays = settings.working_days || [0, 1, 2, 3, 4];
  const overtimeSecs = (Number(settings.standard_workday_hours) || 8) * 3600;

  const loadStatus = async () => {
    // Non-managers only load their own clock; the team roster/status is manager-only.
    if (isManager) {
      const [s, m] = await Promise.all([getTimeClockStatus(), getMyClock()]);
      setStatus(s); setMe(m);
    } else {
      setMe(await getMyClock());
    }
  };
  const loadEntries = async (v, d) => setEntries(await getTimeEntries(rangeFor(v, d)));

  // Weekly overtime/deficit summary + the leave & holiday records affecting the shown window.
  const loadSummaryAndOff = async (v, d, op) => {
    const r = summaryRangeFor(v, d);
    const sumParams = { start: r.start, end: r.end };
    const leaveParams = { start: r.start, end: r.end };
    if (op && op !== "all") { sumParams.operator_id = op; leaveParams.operator_id = op; }
    try {
      const [sm, lv, hd] = await Promise.all([
        getTimeClockSummary(sumParams),
        getLeaves(leaveParams),
        getHolidays({ start: r.start, end: r.end }),
      ]);
      setSummary(sm); setLeaves(lv || []); setHolidays(hd || []);
    } catch (e) { console.error("clock: summary load failed", e); }
  };

  useEffect(() => {
    (async () => {
      await loadStatus();
      await loadEntries("day", todayStr());
      await loadSummaryAndOff("day", todayStr(), "all");
      try { setSettings(await getCompanySettings()); } catch (e) { console.error("clock: settings load failed", e); }
    })();
    // Intentional run-once mount load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload the weekly summary + leave/holiday records whenever the window or employee filter changes.
  useEffect(() => {
    loadSummaryAndOff(view, day, filterOp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, day, filterOp]);

  const refreshAll = async () => { await loadStatus(); await loadEntries(view, day); await loadSummaryAndOff(view, day, filterOp); };

  const doKiosk = async (op, action) => {
    setBusy(true);
    try {
      if (action === "in") { await clockIn(op.operator_id); toast.success(t("clock.clockedInToast", { name: op.name })); }
      else { const r = await clockOut(op.operator_id); toast.success(t("clock.clockedOutToast", { name: op.name, dur: fmtDuration(r.duration_seconds) })); }
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("clock.actionFailed")); }
    finally { setBusy(false); }
  };

  const doSelf = async (action) => {
    setBusy(true);
    try {
      if (action === "in") { await clockInMe(); toast.success(t("clock.clockedInToast", { name: me.operator?.name || "" })); }
      else { const r = await clockOutMe(); toast.success(t("clock.clockedOutToast", { name: me.operator?.name || "", dur: fmtDuration(r.duration_seconds) })); }
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("clock.actionFailed")); }
    finally { setBusy(false); }
  };

  const onDayChange = async (d) => { setDay(d); await loadEntries(view, d); };
  const onViewChange = async (v) => { if (!v) return; setView(v); await loadEntries(v, day); };

  const displayedEntries = useMemo(
    () => entries.filter((e) => {
      if (filterOp !== "all" && e.operator_id !== filterOp) return false;
      if (weekendOnly && workingDays.includes(dowMon(e.day))) return false;
      return true;
    }),
    [entries, filterOp, weekendOnly, workingDays],
  );

  const totalRangeSecs = useMemo(
    () => displayedEntries.reduce((s, e) => s + (e.duration_seconds || 0), 0),
    [displayedEntries],
  );
  const totalBreakSecs = useMemo(
    () => displayedEntries.reduce((s, e) => s + (e.break_seconds || 0), 0),
    [displayedEntries],
  );
  // Overtime & deficit come from the backend weekly (ISO-week) calculation, which nets missed
  // days against mandatory weekly hours and excuses leave / holidays.
  const totalOvertimeSecs = summary.total_overtime_secs || 0;
  const totalDeficitSecs = summary.total_deficit_secs || 0;
  // Distinct operators appearing in the loaded entries (for the employee filter).
  const entryOperators = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => { if (e.operator_id && !m.has(e.operator_id)) m.set(e.operator_id, e.operator_name || ""); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const copyKioskLink = async () => {
    try { await navigator.clipboard.writeText(kioskUrl); toast.success(t("clock.linkCopied")); }
    catch { toast.error(t("clock.actionFailed")); }
  };

  const openAdd = () => {
    const now = new Date();
    setForm({ ...emptyForm, clock_in_at: `${fmtLocalDate(now)}T${pad(now.getHours())}:${pad(now.getMinutes())}` });
    setDialogOpen(true);
  };
  const openEdit = (e) => {
    setForm({
      id: e.id,
      operator_id: e.operator_id,
      clock_in_at: isoToLocalInput(e.clock_in_at),
      clock_out_at: isoToLocalInput(e.clock_out_at),
      break_minutes: e.break_seconds ? String(Math.round(e.break_seconds / 60)) : "",
      notes: e.notes || "",
    });
    setDialogOpen(true);
  };

  const saveEntry = async () => {
    if (!form.clock_in_at) { toast.error(t("clock.saveEntryFailed")); return; }
    const breakMin = form.break_minutes === "" || form.break_minutes == null ? null : Number(form.break_minutes);
    if (breakMin != null && (Number.isNaN(breakMin) || breakMin < 0)) { toast.error(t("clock.saveEntryFailed")); return; }
    setSaving(true);
    try {
      if (form.id) {
        await updateTimeEntry(form.id, {
          clock_in_at: localInputToIso(form.clock_in_at),
          clock_out_at: form.clock_out_at ? localInputToIso(form.clock_out_at) : "",
          break_minutes: breakMin,
          notes: form.notes || "",
        });
        toast.success(t("clock.entryUpdated"));
      } else {
        if (!form.operator_id) { toast.error(t("clock.saveEntryFailed")); setSaving(false); return; }
        await createTimeEntry({
          operator_id: form.operator_id,
          clock_in_at: localInputToIso(form.clock_in_at),
          clock_out_at: form.clock_out_at ? localInputToIso(form.clock_out_at) : null,
          break_minutes: breakMin,
          notes: form.notes || "",
        });
        toast.success(t("clock.entryCreated"));
      }
      setDialogOpen(false);
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("clock.saveEntryFailed")); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteTimeEntry(deleteTarget.id);
      toast.success(t("clock.entryDeleted"));
      setDeleteTarget(null);
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("clock.deleteEntryFailed")); }
    finally { setSaving(false); }
  };

  // ---- Leave & holiday management ----
  const openLeaveForm = () => setLeaveForm({ open: true, operator_id: "", start_date: day, end_date: day, type: "leave", note: "" });
  const openHolidayForm = () => setHolidayForm({ open: true, date: day, name: "" });

  const saveLeave = async () => {
    if (!leaveForm.operator_id || !leaveForm.start_date || !leaveForm.end_date) { toast.error(t("clock.offFillAll")); return; }
    setSavingOff(true);
    try {
      await createLeave({
        operator_id: leaveForm.operator_id,
        start_date: leaveForm.start_date,
        end_date: leaveForm.end_date,
        type: leaveForm.type,
        note: leaveForm.note || "",
      });
      toast.success(t("clock.leaveSaved"));
      setLeaveForm((f) => ({ ...f, open: false }));
      await loadSummaryAndOff(view, day, filterOp);
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSavingOff(false); }
  };

  const removeLeave = async (id) => {
    try { await deleteLeave(id); toast.success(t("clock.leaveDeleted")); await loadSummaryAndOff(view, day, filterOp); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const saveHoliday = async () => {
    if (!holidayForm.date) { toast.error(t("clock.offFillAll")); return; }
    setSavingOff(true);
    try {
      await createHoliday({ date: holidayForm.date, name: holidayForm.name || "" });
      toast.success(t("clock.holidaySaved"));
      setHolidayForm((f) => ({ ...f, open: false }));
      await loadSummaryAndOff(view, day, filterOp);
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSavingOff(false); }
  };

  const removeHoliday = async (id) => {
    try { await deleteHoliday(id); toast.success(t("clock.holidayDeleted")); await loadSummaryAndOff(view, day, filterOp); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const leaveTypeLabel = (ty) => t(`clock.leaveType_${ty}`);

  return (
    <Layout actions={
      <div className="flex items-center gap-2">
        {isManager && (
          <Button size="sm" variant="outline" asChild data-testid="clock-open-kiosk">
            <a href="/kiosk" target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1.5 h-4 w-4" /> {t("clock.openKiosk")}
            </a>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={refreshAll} data-testid="clock-refresh-button">
          <RefreshCw className="mr-1.5 h-4 w-4" /> {t("common.retry")}
        </Button>
      </div>
    }>
      {/* Dashboard summary (managers/admins only) */}
      {isManager && (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card data-testid="clock-stat-in">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]"><Users className="h-5 w-5" /></div>
            <div>
              <div className="text-2xl font-semibold leading-none">{status.clocked_in_count}</div>
              <div className="text-xs text-muted-foreground">{t("clock.nowAtWork")}</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="clock-stat-today">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]"><Timer className="h-5 w-5" /></div>
            <div>
              <div className="text-2xl font-semibold leading-none">{fmtDuration(status.total_today_seconds)}</div>
              <div className="text-xs text-muted-foreground">{t("clock.totalToday")}</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="clock-stat-roster">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]"><Clock className="h-5 w-5" /></div>
            <div>
              <div className="text-2xl font-semibold leading-none">{status.operators.length}</div>
              <div className="text-xs text-muted-foreground">{t("operators.title")}</div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Kiosk link banner (managers/admins) */}
      {isManager && (
        <Card className="mt-4 border-[hsl(var(--brand)/0.35)]" data-testid="clock-kiosk-banner">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]"><ExternalLink className="h-5 w-5" /></div>
              <div>
                <div className="text-sm font-medium">{t("clock.kioskLink")}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{t("clock.kioskHint")}</div>
                <code className="mt-1 block break-all font-mono text-xs text-[hsl(var(--brand))]" data-testid="clock-kiosk-url">{kioskUrl}</code>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button size="sm" variant="outline" onClick={copyKioskLink} data-testid="clock-copy-kiosk">
                <Copy className="mr-1.5 h-4 w-4" /> {t("clock.copyLink")}
              </Button>
              <Button size="sm" asChild data-testid="clock-open-kiosk-banner">
                <a href="/kiosk" target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> {t("clock.openKiosk")}
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My clock */}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">{t("clock.myClock")}</CardTitle></CardHeader>
        <CardContent>
          {!me.operator ? (
            <p className="text-sm text-muted-foreground" data-testid="clock-not-linked">{t("clock.notLinked")}</p>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3" data-testid="clock-my-card">
              <div>
                <div className="text-sm font-medium">{me.operator.name}</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  {me.open_entry ? (
                    <>
                      <Badge className="badge-ok">{t("clock.clockedIn")}</Badge>
                      <span>{t("clock.since", { time: timeOf(me.open_entry.clock_in_at) })}</span>
                    </>
                  ) : (
                    <Badge className="bg-secondary text-muted-foreground border border-border">{t("clock.notClockedIn")}</Badge>
                  )}
                  <span>· {t("clock.todayWorked", { dur: fmtDuration(me.today_seconds) })}</span>
                </div>
              </div>
              {me.open_entry ? (
                <Button disabled={busy} onClick={() => doSelf("out")} variant="outline" data-testid="clock-self-out">
                  <LogOut className="mr-1.5 h-4 w-4" /> {t("clock.clockOut")}
                </Button>
              ) : (
                <Button disabled={busy} onClick={() => doSelf("in")} data-testid="clock-self-in">
                  <LogIn className="mr-1.5 h-4 w-4" /> {t("clock.clockIn")}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue={isManager ? "kiosk" : "history"} className="mt-4">
        <TabsList>
          {isManager && <TabsTrigger value="kiosk" data-testid="clock-tab-kiosk">{t("clock.kiosk")}</TabsTrigger>}
          <TabsTrigger value="history" data-testid="clock-tab-history">{t("clock.history")}</TabsTrigger>
        </TabsList>

        {isManager && (
        <TabsContent value="kiosk">
          {status.operators.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="clock-kiosk-empty">{t("clock.noOperators")}</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {status.operators.map((op) => (
                <Card key={op.operator_id} data-testid={`clock-op-${op.operator_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium">{op.name}</div>
                        <div className="text-xs text-muted-foreground">{op.title || "\u00A0"}</div>
                      </div>
                      {op.on_break ? (
                        <Badge className="badge-warn" data-testid={`clock-onbreak-${op.operator_id}`}>{t("clock.onBreak")}</Badge>
                      ) : op.clocked_in ? (
                        <Badge className="badge-ok">{t("clock.clockedIn")}</Badge>
                      ) : (
                        <Badge className="bg-secondary text-muted-foreground border border-border">{t("clock.notClockedIn")}</Badge>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {op.clocked_in && <span>{t("clock.since", { time: timeOf(op.since) })} · </span>}
                      {t("clock.todayWorked", { dur: fmtDuration(op.today_seconds) })}
                    </div>
                    <div className="mt-3">
                      {op.clocked_in ? (
                        <Button size="sm" variant="outline" className="w-full" disabled={busy} onClick={() => doKiosk(op, "out")} data-testid={`clock-out-${op.operator_id}`}>
                          <LogOut className="mr-1.5 h-4 w-4" /> {t("clock.clockOut")}
                        </Button>
                      ) : (
                        <Button size="sm" className="w-full" disabled={busy} onClick={() => doKiosk(op, "in")} data-testid={`clock-in-${op.operator_id}`}>
                          <LogIn className="mr-1.5 h-4 w-4" /> {t("clock.clockIn")}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        )}

        <TabsContent value="history">
          <Card>
            <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base">{t("clock.history")}</CardTitle>
              <div className="flex flex-wrap items-center gap-3">
                <ToggleGroup type="single" value={view} onValueChange={onViewChange} className="gap-1" data-testid="clock-view-toggle">
                  <ToggleGroupItem value="day" className="h-8 px-3 text-xs" data-testid="clock-view-day">{t("clock.filterDay")}</ToggleGroupItem>
                  <ToggleGroupItem value="week" className="h-8 px-3 text-xs" data-testid="clock-view-week">{t("clock.filterWeek")}</ToggleGroupItem>
                  <ToggleGroupItem value="month" className="h-8 px-3 text-xs" data-testid="clock-view-month">{t("clock.filterMonth")}</ToggleGroupItem>
                </ToggleGroup>
                <Input type="date" value={day} onChange={(e) => onDayChange(e.target.value)} className="h-8 w-40" data-testid="clock-history-day" />
                {isManager && (
                  <Select value={filterOp} onValueChange={setFilterOp}>
                    <SelectTrigger className="h-8 w-44" data-testid="clock-filter-operator"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("clock.allEmployees")}</SelectItem>
                      {entryOperators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <label className="flex h-8 items-center gap-2 text-xs text-muted-foreground" data-testid="clock-filter-weekend-label">
                  <Switch checked={weekendOnly} onCheckedChange={setWeekendOnly} data-testid="clock-filter-weekend" />
                  {t("clock.weekendOnly")}
                </label>
                {isManager && (
                  <Button size="sm" onClick={openAdd} data-testid="clock-add-entry">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("clock.addEntry")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Legend + total */}
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm bg-[hsl(var(--warning)/0.18)] ring-1 ring-[hsl(var(--warning)/0.5)]" />
                    {t("clock.legendWeekend")}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Badge className="badge-warn h-4 px-1.5 text-[10px]">{t("clock.overtimeBadge")}</Badge>
                    {t("clock.legendOvertime", { h: settings.standard_workday_hours ?? 8 })}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center gap-1.5 font-mono font-medium text-foreground" data-testid="clock-range-break">
                    <Coffee className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />
                    {t("clock.totalBreak")}: {fmtDuration(totalBreakSecs)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono font-medium" data-testid="clock-range-overtime">
                    <Timer className={`h-3.5 w-3.5 ${totalOvertimeSecs > 0 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`} />
                    <span className={totalOvertimeSecs > 0 ? "text-[hsl(var(--warning))]" : "text-foreground"}>
                      {t("clock.totalOvertime")}: {fmtDuration(totalOvertimeSecs)}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono font-medium" data-testid="clock-range-deficit">
                    <TrendingDown className={`h-3.5 w-3.5 ${totalDeficitSecs > 0 ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`} />
                    <span className={totalDeficitSecs > 0 ? "text-[hsl(var(--danger))]" : "text-foreground"}>
                      {t("clock.totalDeficit")}: {fmtDuration(totalDeficitSecs)}
                    </span>
                  </span>
                  <span className="font-mono font-medium text-foreground" data-testid="clock-range-total">
                    {t("clock.totalRange")}: {fmtDuration(totalRangeSecs)}
                  </span>
                </div>
              </div>
              <p className="mb-3 text-[11px] text-muted-foreground" data-testid="clock-overtime-basis">
                {t("clock.overtimeBasis", { h: (settings.standard_workday_hours ?? 8) * (workingDays.length || 5) })}
              </p>

              {displayedEntries.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground" data-testid="clock-history-empty">{t("clock.entriesEmptyRange")}</p>
              ) : (
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("clock.date")}</TableHead>
                        <TableHead>{t("clock.operator")}</TableHead>
                        <TableHead>{t("clock.in")}</TableHead>
                        <TableHead>{t("clock.out")}</TableHead>
                        <TableHead>{t("clock.break")}</TableHead>
                        <TableHead>{t("clock.duration")}</TableHead>
                        <TableHead>{t("clock.photo")}</TableHead>
                        <TableHead>{t("clock.source")}</TableHead>
                        {isManager && <TableHead className="text-right">{t("clock.actionsCol")}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayedEntries.map((e) => {
                        const isWeekend = !workingDays.includes(dowMon(e.day));
                        const isOvertime = (e.duration_seconds || 0) > overtimeSecs;
                        return (
                          <TableRow
                            key={e.id}
                            data-testid={`clock-entry-${e.id}`}
                            className={isWeekend ? "bg-[hsl(var(--warning)/0.10)]" : undefined}
                          >
                            <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                              {e.day}
                              {isWeekend && <Badge className="badge-warn ml-1.5 h-4 px-1.5 text-[10px]" data-testid={`clock-weekend-${e.id}`}>{t("clock.weekendBadge")}</Badge>}
                            </TableCell>
                            <TableCell className="font-medium">{e.operator_name}</TableCell>
                            <TableCell>{timeOf(e.clock_in_at)}</TableCell>
                            <TableCell>{e.clock_out_at ? timeOf(e.clock_out_at) : <span className="text-[hsl(var(--brand))]">{t("clock.ongoing")}</span>}</TableCell>
                            <TableCell data-testid={`clock-break-${e.id}`}>
                              {e.on_break ? (
                                <Badge className="badge-warn h-5 gap-1 px-1.5 text-[10px]" data-testid={`clock-onbreak-badge-${e.id}`}>
                                  <Coffee className="h-3 w-3" /> {t("clock.onBreak")}
                                </Badge>
                              ) : (e.break_seconds || 0) > 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                  <Coffee className="h-3 w-3" /> {fmtDuration(e.break_seconds)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={isOvertime ? "font-medium text-[hsl(var(--warning))]" : undefined}>{fmtDuration(e.duration_seconds)}</span>
                              {isOvertime && <Badge className="badge-warn ml-1.5 h-4 px-1.5 text-[10px]" data-testid={`clock-overtime-${e.id}`}>{t("clock.overtimeBadge")}</Badge>}
                            </TableCell>
                            <TableCell>
                              {(e.photo_in_url || e.photo_out_url) ? (
                                <div className="flex items-center gap-1.5">
                                  {e.photo_in_url && <PhotoThumb url={e.photo_in_url} label={t("clock.photoIn")} />}
                                  {e.photo_out_url && <PhotoThumb url={e.photo_out_url} label={t("clock.photoOut")} />}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">{t("clock.noPhoto")}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-muted-foreground">
                                {e.source === "self" ? t("clock.sourceSelf") : e.source === "manual" ? t("clock.sourceManual") : t("clock.sourceKiosk")}
                              </span>
                            </TableCell>
                            {isManager && (
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(e)} data-testid={`clock-edit-${e.id}`} aria-label={t("clock.editEntry")}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => setDeleteTarget(e)} data-testid={`clock-delete-${e.id}`} aria-label={t("common.delete")}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {isManager && (
            <Card className="mt-4" data-testid="clock-timeoff-card">
              <CardHeader className="flex flex-col gap-1 space-y-0 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">{t("clock.timeOffTitle")}</CardTitle>
                  <p className="mt-1 text-xs text-muted-foreground">{t("clock.timeOffHint")}</p>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Employee leave */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                        <Palmtree className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("clock.leavesTitle")}
                      </span>
                      <Button size="sm" variant="outline" onClick={openLeaveForm} data-testid="clock-add-leave">
                        <Plus className="mr-1.5 h-4 w-4" /> {t("clock.addLeave")}
                      </Button>
                    </div>
                    {leaves.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground" data-testid="clock-leaves-empty">{t("clock.leavesEmpty")}</p>
                    ) : (
                      <div className="space-y-1.5" data-testid="clock-leaves-list">
                        {leaves.map((lv) => (
                          <div key={lv.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`clock-leave-${lv.id}`}>
                            <div className="min-w-0">
                              <div className="truncate font-medium">{lv.operator_name}</div>
                              <div className="font-mono text-[11px] text-muted-foreground">
                                {lv.start_date}{lv.end_date !== lv.start_date ? ` – ${lv.end_date}` : ""}
                                <Badge variant="outline" className="ml-1.5 h-4 px-1.5 text-[10px]">{leaveTypeLabel(lv.type)}</Badge>
                                {lv.note ? ` · ${lv.note}` : ""}
                              </div>
                            </div>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => removeLeave(lv.id)} data-testid={`clock-leave-delete-${lv.id}`} aria-label={t("common.delete")}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Company holidays */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                        <CalendarDays className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("clock.holidaysTitle")}
                      </span>
                      <Button size="sm" variant="outline" onClick={openHolidayForm} data-testid="clock-add-holiday">
                        <Plus className="mr-1.5 h-4 w-4" /> {t("clock.addHoliday")}
                      </Button>
                    </div>
                    {holidays.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground" data-testid="clock-holidays-empty">{t("clock.holidaysEmpty")}</p>
                    ) : (
                      <div className="space-y-1.5" data-testid="clock-holidays-list">
                        {holidays.map((hd) => (
                          <div key={hd.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`clock-holiday-${hd.id}`}>
                            <div className="min-w-0">
                              <div className="font-mono text-xs">{hd.date}</div>
                              {hd.name ? <div className="truncate text-[11px] text-muted-foreground">{hd.name}</div> : null}
                            </div>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => removeHoliday(hd.id)} data-testid={`clock-holiday-delete-${hd.id}`} aria-label={t("common.delete")}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add / Edit entry dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="clock-entry-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? t("clock.editEntryTitle") : t("clock.addEntryTitle")}</DialogTitle>
            <DialogDescription>{t("clock.leaveOpenHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="entry-operator">{t("clock.selectOp")}</Label>
              {form.id ? (
                <Input id="entry-operator" value={entries.find((e) => e.id === form.id)?.operator_name || ""} disabled data-testid="clock-form-operator-readonly" />
              ) : (
                <Select value={form.operator_id} onValueChange={(v) => setForm((f) => ({ ...f, operator_id: v }))}>
                  <SelectTrigger id="entry-operator" data-testid="clock-form-operator">
                    <SelectValue placeholder={t("clock.selectOp")} />
                  </SelectTrigger>
                  <SelectContent>
                    {status.operators.map((op) => (
                      <SelectItem key={op.operator_id} value={op.operator_id} data-testid={`clock-form-op-${op.operator_id}`}>{op.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="entry-in">{t("clock.clockInTime")}</Label>
                <Input id="entry-in" type="datetime-local" value={form.clock_in_at}
                  onChange={(e) => setForm((f) => ({ ...f, clock_in_at: e.target.value }))} data-testid="clock-form-in" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="entry-out">{t("clock.clockOutTime")}</Label>
                <Input id="entry-out" type="datetime-local" value={form.clock_out_at}
                  onChange={(e) => setForm((f) => ({ ...f, clock_out_at: e.target.value }))} data-testid="clock-form-out" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry-break" className="flex items-center gap-1.5">
                <Coffee className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("clock.breakMinutes")}
              </Label>
              <Input id="entry-break" type="number" min="0" step="1" value={form.break_minutes}
                placeholder="0"
                onChange={(e) => setForm((f) => ({ ...f, break_minutes: e.target.value }))} data-testid="clock-form-break" />
              <p className="text-xs text-muted-foreground">{t("clock.breakHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry-notes">{t("common.notes")}</Label>
              <Textarea id="entry-notes" value={form.notes} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="clock-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="clock-form-cancel">{t("common.cancel")}</Button>
            <Button onClick={saveEntry} disabled={saving} data-testid="clock-form-save">
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add leave dialog */}
      <Dialog open={leaveForm.open} onOpenChange={(o) => setLeaveForm((f) => ({ ...f, open: o }))}>
        <DialogContent data-testid="clock-leave-dialog">
          <DialogHeader>
            <DialogTitle>{t("clock.addLeave")}</DialogTitle>
            <DialogDescription>{t("clock.leaveDialogHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="leave-op">{t("clock.selectOp")}</Label>
              <Select value={leaveForm.operator_id} onValueChange={(v) => setLeaveForm((f) => ({ ...f, operator_id: v }))}>
                <SelectTrigger id="leave-op" data-testid="clock-leave-operator"><SelectValue placeholder={t("clock.selectOp")} /></SelectTrigger>
                <SelectContent>
                  {status.operators.map((op) => (
                    <SelectItem key={op.operator_id} value={op.operator_id} data-testid={`clock-leave-op-${op.operator_id}`}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="leave-start">{t("clock.leaveStart")}</Label>
                <Input id="leave-start" type="date" value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date && f.end_date >= e.target.value ? f.end_date : e.target.value }))} data-testid="clock-leave-start" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="leave-end">{t("clock.leaveEnd")}</Label>
                <Input id="leave-end" type="date" min={leaveForm.start_date} value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="clock-leave-end" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leave-type">{t("clock.leaveTypeLabel")}</Label>
              <Select value={leaveForm.type} onValueChange={(v) => setLeaveForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger id="leave-type" data-testid="clock-leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leave">{t("clock.leaveType_leave")}</SelectItem>
                  <SelectItem value="sick">{t("clock.leaveType_sick")}</SelectItem>
                  <SelectItem value="unpaid">{t("clock.leaveType_unpaid")}</SelectItem>
                  <SelectItem value="other">{t("clock.leaveType_other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leave-note">{t("common.notes")}</Label>
              <Input id="leave-note" value={leaveForm.note} onChange={(e) => setLeaveForm((f) => ({ ...f, note: e.target.value }))} data-testid="clock-leave-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveForm((f) => ({ ...f, open: false }))} data-testid="clock-leave-cancel">{t("common.cancel")}</Button>
            <Button onClick={saveLeave} disabled={savingOff} data-testid="clock-leave-save">{savingOff ? t("common.saving") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add holiday dialog */}
      <Dialog open={holidayForm.open} onOpenChange={(o) => setHolidayForm((f) => ({ ...f, open: o }))}>
        <DialogContent data-testid="clock-holiday-dialog">
          <DialogHeader>
            <DialogTitle>{t("clock.addHoliday")}</DialogTitle>
            <DialogDescription>{t("clock.holidayDialogHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="holiday-date">{t("clock.holidayDate")}</Label>
              <Input id="holiday-date" type="date" value={holidayForm.date}
                onChange={(e) => setHolidayForm((f) => ({ ...f, date: e.target.value }))} data-testid="clock-holiday-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="holiday-name">{t("clock.holidayName")}</Label>
              <Input id="holiday-name" value={holidayForm.name} onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("clock.holidayNamePh")} data-testid="clock-holiday-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayForm((f) => ({ ...f, open: false }))} data-testid="clock-holiday-cancel">{t("common.cancel")}</Button>
            <Button onClick={saveHoliday} disabled={savingOff} data-testid="clock-holiday-save">{savingOff ? t("common.saving") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="clock-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clock.editEntry")}</AlertDialogTitle>
            <AlertDialogDescription>{t("clock.confirmDeleteEntry")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clock-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={saving}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="clock-delete-confirm">
              {saving ? t("common.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
